import React from "react";
import { connect } from "react-redux";
import type { RootState } from "typesafe-actions";
import { DashboardOption } from "../../enums/DashboardOption.enum";
import { ShareWorkspaceDialogSections } from "../../enums/ShareWorkspaceDialogSections";
import { AppMode } from "../../state/mode/mode.reducer";
import { DashboardAssetPanel } from "../DashboardAssetPanel";
import CompleteAccount from "../prompts/CompleteAccount";
import { AnnouncementsDialog } from "./AnnouncementsDialog";
import ChangeMasterDialog from "./ChangeMasterDialog";
import { ConfirmAccountDelete } from "./ConfirmAccountDelete";
import DeleteTemplateDialog from "./DeleteTemplateDialog";
import DeleteWorkspaceDialog from "./DeleteWorkspaceDialog";
import { DropScopeDialog } from "./DropScopeDialog";
import DuplicateWorkspaceDialog from "./DuplicateWorkspaceDialog";
import DownloadDialog from "./downloadDialog/DownloadDialog";
import EditLabelDialog from "./EditLabelDialog";
import ErrorDialogRouter from "./ErrorDialogRouter";
import FreeUpsellDialog from "./FreeUpsellDialog";
import HideWorkspaceDialog from "./HideWorkspaceDialog";
import { InviteUserDialog } from "./InviteUsersDialog";
import LeaveWorkspaceDialog from "./LeaveWorkspaceDialog";
import LinkedMasterDialog from "./LinkedMasterDialog";
import LoadingWorkspaceDialog from "./LoadingWorkspaceDialog";
import { ManageAccount } from "./ManageAccount";
import NotificationCenterDialog from "./NotificationCenterDialog";
import { NewWorkspacePasswordsDialog } from "./new.workspace.passwords.dialog";
import { NexusInsightsPanelDialog } from "./nexusInsightPanelDialog/NexusInsightsPanelDialog";
import OpenSharedWorkspaceDialog from "./OpenSharedWorkspaceDialog";
import RemoveWorkspaceDialog from "./RemoveWorkspaceDialog";
import RemoveWorkspaceFromProjectDialog from "./RemoveWorkspaceFromProjectDialog";
import { RequestingSnapshotDialog } from "./RequestingSnapshotDialog";
import SearchWorkspaceDialog from "./SearchWorkspaceDialog";
import ShareWorkspaceDialog from "./sharing/ShareWorkspaceDialog";
import { ThumbnailEditorDialog } from "./ThumbnailEditorDialog";
import { TaskLocationsModal } from "./task.locations.modal";
import UploadDialog from "./uploadDialog/UploadDialog";
import { ZendeskWidget } from "./ZendeskWidget";

const mapStateToProps = (state: RootState) => {
  const activeOption = state.context.workspaces.activeOption;
  // state.context.user.isLoggedIn can be either boolean or 'UNKNOWN' so can't cast it to just !!state.context.user.isLoggedIn
  const isLoggedIn =
    typeof state.context.user.isLoggedIn === "boolean" &&
    state.context.user.isLoggedIn;

  return {
    isPartialAccount: state.context.user.profile.isPartialAccount,
    isErrorState: state.mode === AppMode.ERROR,
    isLoading:
      isLoggedIn &&
      state.mode === AppMode.LOADING_WORKSPACE &&
      state.context.config.teamsFrame !== "sidePanel",
    isRequestingSnapshot: activeOption === DashboardOption.REQUESTING_SNAPSHOT,
    isCreatingWorkspace:
      activeOption === DashboardOption.CREATE ||
      activeOption === DashboardOption.CREATE_FROM_TEMPLATE,
    isOpeningSharedWorkspace: activeOption === DashboardOption.OPEN,
    isHiding: activeOption === DashboardOption.HIDE,
    isLeaving: activeOption === DashboardOption.LEAVE,
    isDuplicating: activeOption === DashboardOption.DUPLICATE,
    isEditingLabels: activeOption === DashboardOption.ADD_LABEL,
    isDeleting: activeOption === DashboardOption.DELETE,
    isDroppingScope: activeOption === DashboardOption.DROP_SCOPE,
    isWorkspaceInfoRemoveDialog: activeOption === DashboardOption.REMOVE,
    isRemovingFromProject: activeOption === DashboardOption.REMOVE_FROM_PROJECT,
    isSharing: activeOption === DashboardOption.SHARE,
    isEditingPermissions: activeOption === DashboardOption.PERMISSIONS,

    isShowingInfo: [DashboardOption.INFO, DashboardOption.REMOVE].includes(
      activeOption as DashboardOption,
    ),
    isShowingExpandedShare: activeOption === DashboardOption.EXPANDED_SHARE,
    showUpsell: activeOption === DashboardOption.UPSELL,
    isInviting: activeOption === DashboardOption.INVITE,
    isShowingAnnouncements: activeOption === DashboardOption.ANNOUNCEMENTS,
    assetPanelActive: activeOption === DashboardOption.ASSET_PANEL,
    isDownloading: activeOption === DashboardOption.DOWNLOAD,
    isUploading: activeOption === DashboardOption.UPLOAD,
    isManagingAccount: activeOption === DashboardOption.MANAGE_ACCOUNT,
    isConfirmingAccountRemoval:
      activeOption === DashboardOption.CONFIRM_ACCOUNT_DELETE,
    isShowingLinkedMasterWorkspace:
      activeOption === DashboardOption.LINKED_MASTER,
    isChangingMasterWorkspace: activeOption === DashboardOption.CHANGE_MASTER,
    isEditingThumbnail: state.context.workspaces.isEditingThumbnail,
    isDeletingTemplate: activeOption === DashboardOption.DELETE_TEMPLATE,
    isSearchingWorkspace: activeOption === DashboardOption.SEARCH_WORKSPACE,
    isNotificationCenterOpened:
      activeOption === DashboardOption.NOTIFICATION_CENTER,
    isShowingTaskLocations:
      activeOption === DashboardOption.SHOW_TASK_LOCATIONS,
    isShowingNexusInsightsPanel:
      activeOption === DashboardOption.NEXUS_INSIGHTS_PANEL,
    isShowingZendeskWidget:
      state.context.config.featureFlags.enableZendeskWidget,
  };
};

type ModalsProps = ReturnType<typeof mapStateToProps>;

export const Modals: React.FC<ModalsProps> = ({
  isPartialAccount,
  isCreatingWorkspace,
  isLoading,
  isErrorState,
  isOpeningSharedWorkspace,
  isHiding,
  isLeaving,
  isDroppingScope,
  isDeleting,
  isWorkspaceInfoRemoveDialog,
  isRemovingFromProject,
  isSharing,
  isShowingAnnouncements,
  showUpsell,
  isDuplicating,
  isEditingLabels,
  isInviting,
  isEditingPermissions,
  assetPanelActive,
  isDownloading,
  isUploading,
  isManagingAccount,
  isConfirmingAccountRemoval,
  isShowingInfo,
  isEditingThumbnail,
  isRequestingSnapshot,
  isShowingLinkedMasterWorkspace,
  isChangingMasterWorkspace,
  isDeletingTemplate,
  isSearchingWorkspace,
  isNotificationCenterOpened,
  isShowingExpandedShare,
  isShowingTaskLocations,
  isShowingNexusInsightsPanel,
  isShowingZendeskWidget,
}) => (
  <>
    {isPartialAccount && <CompleteAccount />}
    {isShowingAnnouncements && <AnnouncementsDialog />}
    {isLoading && <LoadingWorkspaceDialog />}
    {isRequestingSnapshot && <RequestingSnapshotDialog />}
    {isOpeningSharedWorkspace && <OpenSharedWorkspaceDialog />}
    {isHiding && <HideWorkspaceDialog />}
    {isLeaving && <LeaveWorkspaceDialog />}
    {isDeleting && <DeleteWorkspaceDialog />}
    {isDroppingScope && <DropScopeDialog />}
    {isWorkspaceInfoRemoveDialog && <RemoveWorkspaceDialog />}

    {isRemovingFromProject && <RemoveWorkspaceFromProjectDialog />}
    {isSharing && (
      <ShareWorkspaceDialog openSection={ShareWorkspaceDialogSections.SHARE} />
    )}
    {isEditingPermissions && (
      <ShareWorkspaceDialog
        openSection={ShareWorkspaceDialogSections.PERMISSION}
      />
    )}
    {isShowingInfo && (
      <ShareWorkspaceDialog openSection={ShareWorkspaceDialogSections.INFO} />
    )}
    {isShowingExpandedShare && (
      <ShareWorkspaceDialog openSection={ShareWorkspaceDialogSections.EVERY} />
    )}
    {isShowingNexusInsightsPanel && <NexusInsightsPanelDialog />}
    {isEditingThumbnail && <ThumbnailEditorDialog />}
    {isDuplicating && <DuplicateWorkspaceDialog />}
    {isCreatingWorkspace && <NewWorkspacePasswordsDialog />}
    {isEditingLabels && <EditLabelDialog />}
    {isErrorState && <ErrorDialogRouter />}
    {showUpsell && <FreeUpsellDialog />}
    {isInviting && <InviteUserDialog />}
    {assetPanelActive && <DashboardAssetPanel />}
    {isDownloading && <DownloadDialog />}
    {isUploading && <UploadDialog />}
    {isManagingAccount && <ManageAccount />}
    {isConfirmingAccountRemoval && <ConfirmAccountDelete />}
    {isShowingLinkedMasterWorkspace && <LinkedMasterDialog />}
    {isChangingMasterWorkspace && <ChangeMasterDialog />}
    {isDeletingTemplate && <DeleteTemplateDialog />}
    {isSearchingWorkspace && <SearchWorkspaceDialog />}
    {isNotificationCenterOpened && <NotificationCenterDialog />}
    {isShowingTaskLocations && <TaskLocationsModal />}
    {isShowingZendeskWidget && <ZendeskWidget />}
  </>
);

export default connect(mapStateToProps)(Modals);
